import { useState, useEffect, useMemo, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import { expensesApi, ExpenseType, ExpenseLog } from '../services/api';
import {
  getMinSelectableDate,
  getMaxSelectableDate,
  formatDateToLocal,
  getEditablePeriodDescription,
} from '../utils/timesheetRules';
import {
  enqueueExpense,
  deleteQueueEntry,
  useExpenseQueue,
} from '../hooks/useExpenseQueue';
import { useCachedResource } from '../hooks/useCachedResource';
import { usePageRefresh } from '../context/PullToRefreshContext';

// Date string → local Date. Handles plain YYYY-MM-DD (form values) AND ISO
// datetime strings like "2026-04-22T00:00:00Z" (server responses). The plain
// form avoids the new Date('YYYY-MM-DD')-as-UTC-midnight pitfall (#1514);
// the ISO form falls back to native parsing then keeps the local date parts.
function parseLocalDate(dateStr: string): Date {
  // Take just the YYYY-MM-DD portion before any 'T' so we always get the
  // calendar date the rep entered, regardless of the server's TZ offset.
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) {
    // Unrecognised format — fall back to native Date and let it best-effort
    return new Date(dateStr);
  }
  return new Date(year, month - 1, day);
}

// Friday of the business week (Sat–Fri) for a given local date. #1514
function getWeekFriday(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const dayOfWeek = d.getDay();
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  const friday = new Date(d);
  friday.setDate(d.getDate() + daysUntilFriday);
  return formatDateToLocal(friday);
}

// Same week test (same Friday-of-week)
function isSameWeek(a: string, b: string): boolean {
  return getWeekFriday(a) === getWeekFriday(b);
}

interface UnifiedRow {
  key: string;
  date: string; // YYYY-MM-DD
  expenseName: string;
  description?: string;
  amount: number;
  filename?: string;
  receiptBlob?: Blob;
  /** Signed URL to the server-stored receipt (synced rows only) */
  receiptUrl?: string;
  // Server-only fields
  serverId?: number;
  // Queue-only fields
  queueId?: string;
  syncStatus?: 'pending' | 'syncing' | 'failed';
  syncError?: string;
}

export default function Expenses() {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);

  // Form state
  const [expenseTypeId, setExpenseTypeId] = useState<number | ''>('');
  const [expenseDate, setExpenseDate] = useState(formatDateToLocal(new Date()));
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [viewDate, setViewDate] = useState(formatDateToLocal(new Date()));

  // Receipt preview modal (#1515)
  const [previewFor, setPreviewFor] = useState<UnifiedRow | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Offline-first queue (#1513)
  const { queueEntries, pendingCount, failedCount, drainNow } = useExpenseQueue();

  const minDate = getMinSelectableDate();
  const maxDate = getMaxSelectableDate();
  const editablePeriod = getEditablePeriodDescription();

  // Load expense types on mount
  useEffect(() => {
    expensesApi.getExpenseTypes().then(setExpenseTypes).catch((err) => {
      console.error('Failed to load expense types', err);
      setError('Failed to load expense types');
    });
  }, []);

  // Cache-first server logs for the viewed week (#2057): paint the last-cached
  // week instantly, then revalidate in the background. The YYYY-MM-DD viewDate
  // is bucketed to its Friday (the timesheet week key).
  const weekFriday = getWeekFriday(viewDate);
  const {
    data: expenseLogsData,
    isLoading,
    error: logsError,
    refetch: reloadLogs,
    mutate: mutateLogs,
  } = useCachedResource<ExpenseLog[]>(
    `expenses:${weekFriday}`,
    () => expensesApi.getExpenseLogs(weekFriday),
    [weekFriday]
  );
  const expenseLogs = expenseLogsData ?? [];

  // #2089: swipe-down refreshes this week's expense logs.
  usePageRefresh(reloadLogs);

  // Re-fetch the server list whenever a queue entry transitions to synced
  // (the queue listener fires `rdm:expense-queue-changed`). #1513
  useEffect(() => {
    const onChange = () => void reloadLogs();
    window.addEventListener('rdm:expense-queue-changed', onChange);
    return () => window.removeEventListener('rdm:expense-queue-changed', onChange);
  }, [reloadLogs]);

  // Handle form submit — enqueue first, then trigger drain. #1513
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!expenseTypeId) return setError('Please select an expense type');
    if (!expenseDate) return setError('Please select a date');
    const parsedAmount = parseFloat(amount);
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) return setError('Please enter a valid amount');

    const type = expenseTypes.find((t) => t.expenseid === expenseTypeId);
    if (!type) return setError('Invalid expense type');

    try {
      await enqueueExpense({
        expenseTypeId: expenseTypeId as number,
        expenseTypeName: type.expensename,
        expenseDate,
        amount: parsedAmount,
        description: description.trim() || undefined,
        receiptBlob: file ?? undefined,
        receiptFilename: file?.name,
        receiptMimeType: file?.type,
      });

      // Snap the view to the entered date's week so the optimistic row is visible
      if (!isSameWeek(viewDate, expenseDate)) {
        setViewDate(expenseDate);
      }

      // Reset form
      setExpenseTypeId('');
      setAmount('');
      setDescription('');
      setFile(null);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';

      setSuccess(navigator.onLine ? 'Expense saved — syncing…' : 'Expense saved — will sync when back online');
      setTimeout(() => setSuccess(null), 3500);

      // Kick a drain immediately
      void drainNow();
    } catch (err) {
      console.error('Failed to enqueue expense', err);
      setError(err instanceof Error ? err.message : 'Failed to save expense');
    }
  };

  // Delete handler — pending queue entry: remove from IDB; synced server entry: DELETE then refresh.
  const handleDelete = async (row: UnifiedRow) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    try {
      if (row.queueId) {
        await deleteQueueEntry(row.queueId);
      } else if (row.serverId) {
        await expensesApi.deleteExpenseLog(row.serverId);
        mutateLogs((prev) => (prev ?? []).filter((l) => l.expenselogid !== row.serverId));
      }
      setSuccess('Expense deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to delete expense', err);
      setError(err instanceof Error ? err.message : 'Failed to delete expense');
    }
  };

  // Receipt preview helpers (#1515)
  const openPreview = (row: UnifiedRow) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = row.receiptBlob ? URL.createObjectURL(row.receiptBlob) : null;
    setPreviewFor(row);
  };
  const closePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewFor(null);
  };
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // Build the unified list — server entries for the viewed week plus all queue entries
  // whose expenseDate falls in the same week. Queue entries are appended to the bottom
  // of the list with a status badge, but sorted by date for readability.
  const unifiedRows: UnifiedRow[] = useMemo(() => {
    const fridayOfView = getWeekFriday(viewDate);
    const serverRows: UnifiedRow[] = expenseLogs.map((log) => ({
      key: `srv-${log.expenselogid}`,
      serverId: log.expenselogid,
      date: log.date,
      expenseName: log.expenseName,
      description: log.description ?? undefined,
      amount: log.amount,
      filename: log.filename || undefined,
      receiptUrl: log.blobUrl ?? undefined,
    }));
    const queueRows: UnifiedRow[] = queueEntries
      .filter((q) => getWeekFriday(q.expenseDate) === fridayOfView)
      .map((q) => ({
        key: `q-${q.id}`,
        queueId: q.id,
        date: q.expenseDate,
        expenseName: q.expenseTypeName,
        description: q.description,
        amount: q.amount,
        filename: q.receiptFilename,
        receiptBlob: q.receiptBlob,
        syncStatus: q.syncStatus,
        syncError: q.syncError,
      }));
    const all = [...serverRows, ...queueRows];
    all.sort((a, b) => b.date.localeCompare(a.date));
    return all;
  }, [expenseLogs, queueEntries, viewDate]);

  const totalAmount = unifiedRows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="min-h-screen w-full max-w-full bg-slate-50 pb-24 overflow-x-hidden">
      <PageHeader title="Expenses" subtitle="Track your expenses" />

      <main className="w-full max-w-full box-border p-4 overflow-hidden">
        {/* Editable Period Notice */}
        <div className="mb-4 rounded-xl bg-slate-100 p-3">
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-slate-600">
              Valid dates: <span className="font-medium">{editablePeriod}</span>
            </span>
          </div>
          <p className="mt-1 text-xs text-slate-500">Cutoff: Sunday 11:59 PM Eastern Time</p>
        </div>

        {/* Sync queue indicator (#1513) */}
        {(pendingCount > 0 || failedCount > 0) && (
          <div className={`mb-4 rounded-xl p-3 ${failedCount > 0 ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                {pendingCount > 0 && <span className="font-medium">{pendingCount} expense{pendingCount === 1 ? '' : 's'} syncing…</span>}
                {failedCount > 0 && <span className="font-medium ml-2">{failedCount} failed</span>}
              </div>
              <button
                type="button"
                onClick={() => drainNow()}
                className="rounded-md bg-white/60 px-3 py-1 text-xs font-medium hover:bg-white"
              >
                Retry now
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-xl bg-green-50 p-4 text-green-700">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {success}
            </div>
          </div>
        )}

        {(error || logsError) && (
          <div className="mb-4 rounded-xl bg-red-50 p-4 text-red-700">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error || 'Failed to load expenses (server unavailable — pending entries shown below)'}
            </div>
          </div>
        )}

        {/* Add Expense Form */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card overflow-hidden">
          <h2 className="mb-4 font-heading text-lg font-semibold text-slate-800">Add Expense</h2>
          <form onSubmit={handleSubmit} className="w-full max-w-full space-y-4">
            <div>
              <label htmlFor="expenseDate" className="mb-2 block text-sm font-medium text-slate-700">Date</label>
              <input
                id="expenseDate"
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
                min={formatDateToLocal(minDate)}
                max={formatDateToLocal(maxDate)}
                className="w-full max-w-full box-border rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            <div>
              <label htmlFor="expenseType" className="mb-2 block text-sm font-medium text-slate-700">Expense Type</label>
              <select
                id="expenseType"
                value={expenseTypeId}
                onChange={(e) => setExpenseTypeId(e.target.value ? Number(e.target.value) : '')}
                className="w-full max-w-full box-border rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">Select expense type...</option>
                {expenseTypes.map((type) => (
                  <option key={type.expenseid} value={type.expenseid}>{type.expensename}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="amount" className="mb-2 block text-sm font-medium text-slate-700">Amount ($)</label>
              <input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full max-w-full box-border rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            <div>
              <label htmlFor="description" className="mb-2 block text-sm font-medium text-slate-700">Description (optional)</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Enter description..."
                className="w-full max-w-full box-border rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Receipt (optional)</label>
              <input
                ref={cameraInputRef}
                id="receipt-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) setFile(f);
                }}
                className="hidden"
              />
              <input
                ref={galleryInputRef}
                id="receipt-gallery"
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) setFile(f);
                }}
                className="hidden"
              />

              {!file ? (
                <div
                  className="w-full max-w-full box-border rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 transition-colors hover:border-primary-400 hover:bg-primary-50"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-500', 'bg-primary-50'); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
                    const f = e.dataTransfer.files?.[0];
                    if (f && (f.type.startsWith('image/') || f.type === 'application/pdf')) setFile(f);
                  }}
                >
                  <div className="flex gap-2 w-full max-w-full">
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex flex-1 min-w-0 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-white py-3 text-sm font-medium text-slate-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
                    >
                      <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Camera</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => galleryInputRef.current?.click()}
                      className="flex flex-1 min-w-0 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-white py-3 text-sm font-medium text-slate-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
                    >
                      <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Gallery</span>
                    </button>
                  </div>
                  <p className="mt-3 text-center text-xs text-slate-400">or drag and drop an image or PDF here</p>
                </div>
              ) : (
                <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  {file.type.startsWith('image/') ? (
                    <div className="relative">
                      <img src={URL.createObjectURL(file)} alt="Receipt preview" className="max-h-48 w-full object-contain" />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <p className="truncate text-sm font-medium text-white">{file.name}</p>
                        <p className="text-xs text-white/70">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-red-100">
                        <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">{file.name}</p>
                        <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      if (cameraInputRef.current) cameraInputRef.current.value = '';
                      if (galleryInputRef.current) galleryInputRef.current.value = '';
                    }}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white shadow-lg opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <button type="submit" className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white transition-colors hover:bg-primary-700">
              Save Expense
            </button>
          </form>
        </div>

        {/* Expense History */}
        <div className="rounded-xl bg-white p-4 shadow-card overflow-hidden">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-lg font-semibold text-slate-800">Expense History</h2>
            <input
              type="date"
              value={viewDate}
              onChange={(e) => setViewDate(e.target.value)}
              className="w-full sm:w-auto rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none"
            />
          </div>

          {isLoading && unifiedRows.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
            </div>
          ) : unifiedRows.length === 0 ? (
            <div className="py-8 text-center text-slate-500">
              <svg className="mx-auto mb-2 h-12 w-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
              </svg>
              <p>No expenses for this week</p>
            </div>
          ) : (
            <>
              <div className="mb-4 rounded-lg bg-slate-50 p-3 text-center">
                <span className="text-sm text-slate-500">Weekly Total: </span>
                <span className="text-lg font-bold text-slate-800">${totalAmount.toFixed(2)}</span>
              </div>

              <div className="space-y-3">
                {unifiedRows.map((row) => {
                  const hasReceipt = !!row.filename || !!row.receiptBlob;
                  const dateLabel = parseLocalDate(row.date).toLocaleDateString();
                  return (
                    <div key={row.key} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800 truncate">{row.expenseName}</span>
                            {(hasReceipt || row.receiptUrl) && (
                              <button
                                type="button"
                                onClick={() => openPreview(row)}
                                title={`View receipt: ${row.filename ?? ''}`}
                                className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-200"
                              >
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                Receipt
                              </button>
                            )}
                            {row.syncStatus === 'pending' && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                Syncing
                              </span>
                            )}
                            {row.syncStatus === 'syncing' && (
                              <span className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                Sending
                              </span>
                            )}
                            {row.syncStatus === 'failed' && (
                              <span title={row.syncError ?? ''} className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                Failed
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-slate-500 break-words">
                            {dateLabel}
                            {row.description && ` — ${row.description}`}
                          </div>
                          {row.syncStatus === 'failed' && row.syncError && (
                            <div className="mt-1 text-xs text-amber-700 break-words">{row.syncError}</div>
                          )}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <span className="font-semibold text-slate-800 whitespace-nowrap">${row.amount.toFixed(2)}</span>
                          <button
                            onClick={() => handleDelete(row)}
                            title="Delete expense"
                            className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Receipt preview modal (#1515) */}
      {previewFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={closePreview}>
          <div className="relative max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-semibold text-slate-800">Receipt</h3>
                <p className="text-xs text-slate-500">{previewFor.filename ?? 'Attached file'}</p>
              </div>
              <button type="button" onClick={closePreview} className="rounded-lg bg-slate-100 px-3 py-1 text-sm hover:bg-slate-200">Close</button>
            </div>
            {previewFor.receiptBlob && previewUrlRef.current ? (
              previewFor.receiptBlob.type.startsWith('image/') ? (
                <img src={previewUrlRef.current} alt="Receipt" className="mx-auto max-h-[75vh]" />
              ) : (
                <iframe src={previewUrlRef.current} title="Receipt PDF" className="h-[75vh] w-full" />
              )
            ) : previewFor.receiptUrl ? (
              previewFor.filename?.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewFor.receiptUrl} title="Receipt PDF" className="h-[75vh] w-full" />
              ) : (
                <img src={previewFor.receiptUrl} alt="Receipt" className="mx-auto max-h-[75vh]" />
              )
            ) : previewFor.serverId ? (
              <p className="py-8 text-center text-sm text-slate-600">
                Receipt URL not provided by server. Filename: <code className="bg-slate-100 px-2 py-0.5 rounded">{previewFor.filename}</code>
              </p>
            ) : (
              <p className="py-8 text-center text-sm text-slate-500">No preview available.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
